// Navigation compat layer over React Router (ADR-017).
//
// The migration off Next replaced the router, not the app: 23 route components
// were already client components doing their own data fetching, and their only
// coupling to Next was `useRouter` / `usePathname` / `useSearchParams` /
// `useParams` from `next/navigation`. Re-implementing that small surface here
// let those files change an import path instead of their logic, which keeps the
// migration diff mechanical and reviewable.
//
// This is a compat layer, not a permanent abstraction. New code should prefer
// React Router's own hooks directly.

import { useLocation, useNavigate, useSearchParams as useRRSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

export { useParams } from "react-router-dom";

interface Router {
  push: (href: string) => void;
  replace: (href: string) => void;
  back: () => void;
  /** See the note on `refresh` below — this is not Next's semantics. */
  refresh: () => void;
}

/**
 * Next's `useRouter()`, backed by React Router.
 *
 * `push` and `replace` map directly onto `navigate`. **`refresh` does not.**
 * Under the App Router it re-fetched the server-rendered payload for the
 * current route; there is no server render here, so the honest client
 * equivalent is "re-fetch the data this view is showing" — an invalidation of
 * the TanStack Query cache.
 *
 * Every existing `refresh()` call site sits immediately after a `replace()`
 * that follows a session change (sign-in, sign-out, password change), where the
 * intent was to make the new cookie take effect. Invalidating gives that intent
 * a real implementation instead of a silent no-op.
 */
export function useRouter(): Router {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return React.useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      refresh: () => void qc.invalidateQueries(),
    }),
    [navigate, qc],
  );
}

/** Next's `usePathname()` — the current path, without search or hash. */
export function usePathname(): string {
  return useLocation().pathname;
}

/**
 * Next's `useSearchParams()`.
 *
 * React Router returns a `[params, setParams]` tuple; Next returns the params
 * alone, which is what the call sites expect. Read-only by construction — no
 * caller currently writes search params.
 */
export function useSearchParams(): URLSearchParams {
  return useRRSearchParams()[0];
}
